<script setup lang="ts">
import { HomeKpiGrid, HomeQuickLinks, TraceIdAlert, useSession, type KpiCard, type QuickLink } from '@yc/ui-kit'
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import { getHomeStats, type HomeStats } from '../api/home'

const { me } = useSession()
const router = useRouter()

const loading = ref(false)
const error = ref<unknown>(null)

const greeting = () => {
  const h = new Date().getHours()
  if (h < 6) return '夜深了'
  if (h < 12) return '早上好'
  if (h < 14) return '中午好'
  if (h < 18) return '下午好'
  return '晚上好'
}

/** 卡片标签：后端 JSON 字段 → 页面文案 */
const defs: Array<{ key: keyof HomeStats; label: string; money?: boolean }> = [
  { key: 'today_orders', label: '今日订单' },
  { key: 'in_transit_orders', label: '在途订单' },
  { key: 'unsettled_count', label: '待结算笔数' },
  { key: 'unsettled_amount', label: '待结算金额', money: true },
  { key: 'draft_settlements', label: '待确认结算单' },
]

// 金额卡统一在这里格式化：此前是模板里 `stats && (card.money ? stats[key].toFixed(2) : ...)`，
// stats 为 null 时整块渲染成空白，与其余子系统的「—」不一致；取不到值时 .toFixed 还会直接抛。
const cards = ref<KpiCard[]>(defs.map((d) => ({ key: d.key, label: d.label, value: '—' })))

const links: QuickLink[] = [
  { to: '/orders', label: '运输订单', perm: 'ops:order' },
  { to: '/settlements', label: '结算管理', perm: 'ops:settle' },
  { to: '/reports', label: '应收报表', perm: 'ops:report' },
]

onMounted(async () => {
  loading.value = true
  error.value = null
  try {
    const s = await getHomeStats()
    cards.value = defs.map((d) => ({
      key: d.key,
      label: d.label,
      value: d.money ? Number(s[d.key] ?? 0).toFixed(2) : (s[d.key] ?? '—'),
    }))
  } catch (e) {
    error.value = e
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div class="yc-home">
    <header class="yc-home__hero">
      <h2 class="yc-home__greeting">{{ greeting() }}，{{ me?.name || me?.username }}</h2>
      <p class="yc-home__sub">
        {{ me?.tenant?.name }}（{{ me?.tenant?.code }}）· {{ me?.username }}
      </p>
    </header>

    <div v-if="error" class="yc-home__error">
      <TraceIdAlert :error="error" />
    </div>

    <HomeKpiGrid :cards="cards" :loading="loading" />

    <HomeQuickLinks :links="links" @navigate="router.push" />
  </div>
</template>

<style scoped lang="scss">
.yc-home {
  &__hero {
    margin-bottom: var(--yc-space-6);
  }

  &__greeting {
    margin: 0;
    font-size: var(--yc-font-2xl);
    font-weight: 600;
  }

  &__sub {
    margin: var(--yc-space-2) 0 0;
    color: var(--yc-text-secondary);
    font-size: var(--yc-font);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }
}
</style>
